import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Plus, Trash2, Flame, Sparkles, Utensils, Loader2, Lock, Wand2, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomTabBar from "../components/BottomTabBar";

const MODES = [
  { id: "truth_or_dare", label: "Truth or Dare", icon: Flame, color: "#FF4D8D" },
  { id: "quiz", label: "Think you know me 💭", icon: Sparkles, color: "#7B61FF" },
  { id: "date_night", label: "Date Night", icon: Utensils, color: "#00E5FF" },
];

const SPICE = ["cute", "flirty", "spicy"];

export default function Questions() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    api.listCustomQuestions()
      .then(setItems)
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  const del = async (id) => {
    await api.deleteCustomQuestion(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const onAdded = (q) => {
    setItems((prev) => [q, ...prev]);
    setAdding(false);
    toast.success("Saved!");
  };

  if (authLoading || loading) {
    return (
      <div className="warm-bg min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF4D8D]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="warm-bg min-h-screen">
        <Header />
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <Lock className="w-10 h-10 text-[#7B61FF] mx-auto mb-5" />
          <h1 className="font-display text-3xl font-bold text-white mb-3">
            Sign in to write your <span className="gradient-text">own</span> questions
          </h1>
          <p className="text-[#C9C7D9] mb-7">
            Your custom questions are saved to your account and can be used in any room.
          </p>
          <Link
            to="/login"
            className="inline-block btn-gradient px-8 py-3.5 rounded-full font-bold"
            data-testid="questions-login-link"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="warm-bg min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 text-[#C9C7D9] hover:text-white font-semibold mb-6"
          data-testid="questions-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
              Your <span className="gradient-text">questions</span>
            </h1>
            <p className="text-[#C9C7D9]">
              Personal truths, dares, quiz Qs & date ideas. Use them in any room.
            </p>
          </div>
          <button
            onClick={() => setAdding(true)}
            className="btn-gradient inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold"
            data-testid="questions-add-button"
          >
            <Plus className="w-4 h-4" /> New
          </button>
        </div>

        {/* AI relationship pack CTA */}
        <button
          type="button"
          onClick={() => setGenerating(true)}
          className="w-full mb-6 text-left rounded-3xl p-6 relative overflow-hidden group transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.005]"
          style={{
            background: "linear-gradient(135deg, rgba(255,77,141,0.15) 0%, rgba(0,229,255,0.12) 100%), rgba(26,26,36,0.75)",
            border: "1px solid rgba(255,77,141,0.35)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(255,77,141,0.2)",
            backdropFilter: "blur(20px)",
          }}
          data-testid="generate-pack-cta"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-40 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, #FF4D8D 0%, transparent 70%)" }} />
          <div className="relative flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF4D8D] via-[#7B61FF] to-[#00E5FF] flex items-center justify-center pulse-glow">
              <Wand2 className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-1">New · AI magic</p>
              <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-1">Generate a pack from your relationship ✨</h3>
              <p className="text-sm text-[#C9C7D9]">
                Tell us a bit about you two. We'll craft truths, dares, quiz questions and date ideas that feel written just for you.
              </p>
            </div>
          </div>
        </button>

        <AnimatePresence>
          {generating && (
            <GeneratePackModal
              onClose={() => setGenerating(false)}
              onDone={(newItems) => {
                setItems((prev) => [...newItems, ...prev]);
                setGenerating(false);
                toast.success(`${newItems.length} personalised questions added 🎁`);
              }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {adding && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <AddForm onCancel={() => setAdding(false)} onAdded={onAdded} />
            </motion.div>
          )}
        </AnimatePresence>

        {items.length === 0 && !adding && (
          <div className="card-glow rounded-3xl p-12 text-center">
            <Sparkles className="w-10 h-10 text-[#00E5FF] mx-auto mb-4" />
            <p className="text-white font-semibold mb-2">No custom questions yet</p>
            <p className="text-[#C9C7D9] text-sm mb-6">Tap "New" above to add your first one.</p>
          </div>
        )}

        <div className="space-y-3 mt-6">
          {items.map((q) => (
            <QuestionItem key={q.id} q={q} onDelete={() => del(q.id)} />
          ))}
        </div>
        <div className="h-44" aria-hidden />
      </main>
      <BottomTabBar active="profile" />
    </div>
  );
}

function modeMeta(mode) {
  return MODES.find((m) => m.id === mode) || MODES[0];
}

function QuestionItem({ q, onDelete }) {
  const m = modeMeta(q.mode);
  const Icon = m.icon;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="card-glow rounded-2xl p-5 flex gap-4 items-start"
      data-testid={`question-item-${q.id}`}
    >
      <div
        className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: `${m.color}20`, border: `1px solid ${m.color}40` }}
      >
        <Icon className="w-5 h-5" style={{ color: m.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: m.color }}>
            {m.label}
          </span>
          <span className="text-xs text-[#C9C7D9]">· {q.spice_level}</span>
        </div>
        <QuestionPreview mode={q.mode} content={q.content} />
      </div>
      <button
        onClick={onDelete}
        className="flex-shrink-0 text-[#C9C7D9] hover:text-[#FF4D8D] p-2 -mr-2 -mt-2 rounded-full hover:bg-white/5 transition"
        data-testid={`delete-question-${q.id}`}
        aria-label="Delete"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

function QuestionPreview({ mode, content }) {
  if (mode === "truth_or_dare") {
    return (
      <div className="space-y-1">
        <p className="text-white text-sm">
          <span className="text-[#FF4D8D] font-bold">Truth: </span>
          {content.truth}
        </p>
        <p className="text-white text-sm">
          <span className="text-[#7B61FF] font-bold">Dare: </span>
          {content.dare}
        </p>
      </div>
    );
  }
  if (mode === "quiz") {
    return <p className="text-white font-semibold">{content.question}</p>;
  }
  return (
    <div>
      <p className="text-white font-semibold">{content.title}</p>
      <p className="text-[#C9C7D9] text-sm mt-0.5">{content.description}</p>
    </div>
  );
}

function AddForm({ onCancel, onAdded }) {
  const [mode, setMode] = useState("truth_or_dare");
  const [spice, setSpice] = useState("flirty");
  const [truth, setTruth] = useState("");
  const [dare, setDare] = useState("");
  const [question, setQuestion] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let content;
      if (mode === "truth_or_dare") content = { truth, dare };
      else if (mode === "quiz") content = { question };
      else content = { title, description, category: category || "At Home" };
      const q = await api.createCustomQuestion({ mode, spice_level: spice, content });
      onAdded(q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-glow rounded-3xl p-6 md:p-7 mb-8 space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#C9C7D9] mb-3">Mode</p>
        <div className="grid grid-cols-3 gap-2">
          {MODES.map((m) => {
            const Icon = m.icon;
            return (
              <button
                type="button"
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`addform-mode-${m.id}`}
                className={`py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-1.5 transition ${
                  mode === m.id ? "text-white" : "bg-white/5 text-[#C9C7D9] hover:bg-white/10"
                }`}
                style={mode === m.id ? {
                  background: `${m.color}22`,
                  border: `1px solid ${m.color}`,
                  boxShadow: `0 0 20px ${m.color}44`,
                } : undefined}
              >
                <Icon className="w-4 h-4" style={mode === m.id ? { color: m.color } : {}} />
                <span className="truncate">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#C9C7D9] mb-3">Vibe</p>
        <div className="grid grid-cols-3 gap-2">
          {SPICE.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => setSpice(s)}
              data-testid={`addform-spice-${s}`}
              className={`py-2.5 rounded-2xl font-semibold text-sm capitalize transition ${
                spice === s ? "btn-gradient text-white" : "bg-white/5 text-[#C9C7D9] hover:bg-white/10"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {mode === "truth_or_dare" && (
        <>
          <TextArea label="Truth" value={truth} onChange={setTruth} placeholder="Ask something real…" testid="addform-truth" />
          <TextArea label="Dare" value={dare} onChange={setDare} placeholder="Challenge me…" testid="addform-dare" />
        </>
      )}
      {mode === "quiz" && (
        <TextArea label="Quiz question" value={question} onChange={setQuestion} placeholder="What's my ideal…" testid="addform-question" />
      )}
      {mode === "date_night" && (
        <>
          <Input label="Title" value={title} onChange={setTitle} placeholder="Candlelit picnic" testid="addform-title" />
          <TextArea label="Description" value={description} onChange={setDescription} placeholder="Pack a blanket, wine, and your favourite snacks…" testid="addform-description" />
          <Input label="Category (optional)" value={category} onChange={setCategory} placeholder="Outdoors, Cozy, Adventure…" testid="addform-category" />
        </>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 btn-gradient rounded-full py-3 font-bold disabled:opacity-60 active:scale-95 transition"
          data-testid="addform-submit"
        >
          {saving ? "Saving..." : "Save question"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-3 rounded-full font-semibold text-[#C9C7D9] hover:text-white hover:bg-white/5 transition"
          data-testid="addform-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#C9C7D9] mb-2 ml-1 uppercase tracking-[0.2em]">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white placeholder:text-[#C9C7D9]/40 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60"
        data-testid={testid}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#C9C7D9] mb-2 ml-1 uppercase tracking-[0.2em]">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 text-white placeholder:text-[#C9C7D9]/40 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60 resize-none"
        data-testid={testid}
      />
    </div>
  );
}


function GeneratePackModal({ onClose, onDone }) {
  const [partner, setPartner] = useState("");
  const [length, setLength] = useState("");
  const [howMet, setHowMet] = useState("");
  const [loveLanguage, setLoveLanguage] = useState("");
  const [insideJokes, setInsideJokes] = useState("");
  const [interests, setInterests] = useState("");
  const [spice, setSpice] = useState("flirty");
  const [generating, setGenerating] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!partner.trim()) {
      toast.error("Partner's name helps — just one word is fine.");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.generateRelationshipPack({
        partner_name: partner.trim(),
        relationship_length: length.trim() || null,
        how_met: howMet.trim() || null,
        love_language: loveLanguage.trim() || null,
        inside_jokes: insideJokes.trim() || null,
        shared_interests: interests.trim() || null,
        spice_level: spice,
        items_per_mode: 4,
      });
      onDone(res.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI couldn't generate. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.form
        initial={{ y: 40, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card-glow rounded-3xl p-6 md:p-8 w-full max-w-lg relative my-8"
        data-testid="generate-pack-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#C9C7D9] hover:text-white transition"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF4D8D] via-[#7B61FF] to-[#00E5FF] flex items-center justify-center pulse-glow">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white tracking-tight">Tell us about you two</h3>
            <p className="text-xs text-[#C9C7D9]">We'll craft 12 personalised questions.</p>
          </div>
        </div>

        <div className="space-y-3">
          <MiniInput label="Partner's name *" value={partner} onChange={setPartner} placeholder="Jordan" testid="gen-partner" />
          <MiniInput label="Relationship length" value={length} onChange={setLength} placeholder="2 years, 6 months…" testid="gen-length" />
          <MiniTextArea label="How you met" value={howMet} onChange={setHowMet} placeholder="Matched on Hinge. First date: 6-hour coffee." testid="gen-howmet" />
          <MiniInput label="Love language" value={loveLanguage} onChange={setLoveLanguage} placeholder="Words of affirmation, Quality time…" testid="gen-love" />
          <MiniTextArea label="Inside jokes" value={insideJokes} onChange={setInsideJokes} placeholder="We always say we have a pact with the barista." testid="gen-jokes" />
          <MiniTextArea label="Shared interests" value={interests} onChange={setInterests} placeholder="Boba, indie films, hiking." testid="gen-interests" />

          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#C9C7D9] mb-2">Vibe</p>
            <div className="grid grid-cols-3 gap-2">
              {["cute", "flirty", "spicy"].map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSpice(s)}
                  data-testid={`gen-spice-${s}`}
                  className={`py-2.5 rounded-2xl font-semibold text-sm capitalize transition ${
                    spice === s ? "btn-gradient text-white" : "bg-white/5 text-[#C9C7D9] hover:bg-white/10"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <button
            type="submit"
            disabled={generating}
            className="btn-gradient rounded-full py-3.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95 transition"
            data-testid="gen-submit"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Crafting…</> : <><Wand2 className="w-4 h-4" /> Generate pack</>}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={generating}
            className="bg-white/5 border border-white/10 text-white rounded-full py-3.5 font-bold active:scale-95 transition disabled:opacity-50"
            data-testid="gen-cancel"
          >
            Cancel
          </button>
        </div>
        <p className="text-[10px] text-[#C9C7D9]/70 mt-4 text-center">
          Takes 10–20s. Items save to your custom library under "Mine" source.
        </p>
      </motion.form>
    </motion.div>
  );
}

function MiniInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-[#C9C7D9] mb-1 ml-1 uppercase tracking-[0.2em]">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-[#C9C7D9]/40 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60 text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function MiniTextArea({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-[#C9C7D9] mb-1 ml-1 uppercase tracking-[0.2em]">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-[#C9C7D9]/40 focus:outline-none focus:ring-2 focus:ring-[#FF4D8D]/60 text-sm resize-none"
        data-testid={testid}
      />
    </div>
  );
}
